import * as R from 'ramda'
import { createSelector } from 'reselect'
import moment from 'moment'
import * as RE from 'helpers/ramda-ext'
import { hasVisibleDepartureDatePicker } from 'domain/packages'
import { getCurrentPackageFilteredDepartureDates } from 'domain/pricerDepartureDate'
import { getFromLocalStorage } from 'store/reducers/fromLocalStorage'
import { getIsHideReferencePriceFeatureEnabled, getIsToggleCheapestPriceFeatureEnabled } from './application'
import {
  getActivePackage,
  getActivePackagePriceInfos,
  getCurrentSelection,
  getIsCheapestPriceToggled,
  getPackageElements,
  getPriceInfosFromActivePackageInFilteredProducts
} from './package'

export const getAvailableDepartureDates = createSelector(
  getPackageElements,
  packageElements => getCurrentPackageFilteredDepartureDates(packageElements)
)

export const getHasOnlyOneDepartureDate = createSelector(
  getAvailableDepartureDates,
  RE.hasOneElement
)

export const getSelectedDepartureDate = createSelector(
  getCurrentSelection,
  getAvailableDepartureDates,
  (currentSelection, availablePackageElements) => {
    if (!currentSelection || !currentSelection.checkin) { return null }

    return availablePackageElements.find((packageElement) => (
      packageElement.checkin === currentSelection.checkin
    ))
  }
)

export const getDepartureDatesByMonth = createSelector(
  getAvailableDepartureDates,
  getSelectedDepartureDate,
  getIsHideReferencePriceFeatureEnabled,
  (availableDepartureDates, selectedDepartureDate, shouldHideReferencePrice) => {
    if (!availableDepartureDates.length) {
      return []
    }

    const comparisonDate = moment(availableDepartureDates[0].checkin).startOf('month')

    const result = availableDepartureDates.reduce((acc, value) => {
      const checkin = moment(value.checkin).startOf('month')

      if (!checkin.isSame(acc.comparisonDate, 'months')) {
        const monthsNumberDiff = checkin.diff(acc.comparisonDate, 'months')
        for (let i = 0; i < monthsNumberDiff; i++) {
          acc.index++
          acc.allDepartureDates[acc.index] = []
        }
        acc.comparisonDate = checkin
      }

      if (!acc.allDepartureDates[acc.index]) {
        acc.allDepartureDates[acc.index] = []
      }

      acc.allDepartureDates[acc.index].push({
        ...value,
        isSelected: value.checkin === (selectedDepartureDate && selectedDepartureDate.checkin),
        msrp: shouldHideReferencePrice ? undefined : value.msrp
      })
      return acc
    }, { allDepartureDates: [], comparisonDate, index: 0 })

    return result.allDepartureDates
  }
)

export const getAvailableMonthList = createSelector(
  getDepartureDatesByMonth,
  departureDatesByMonth => {
    if (!departureDatesByMonth.length) {
      return []
    }

    let firstDate = moment(departureDatesByMonth[0][0].checkin)

    return departureDatesByMonth.map(departureDate => {
      const newMonth = ({
        isDisabled: !(departureDate.length > 0),
        month: firstDate.format('MMMM'),
        shortMonth: firstDate.format('MMM'),
        year: firstDate.format('YYYY')
      })

      firstDate = firstDate.add(1, 'month')

      return newMonth
    })
  }
)

export const getNbAvailableDepartureDatesWithStock = createSelector(
  getAvailableDepartureDates,
  R.pipe(
    R.filter(value => value.stock),
    R.length
  )
)

export const getSelectedCheckin = createSelector(
  getSelectedDepartureDate,
  RE.whenNotNil(R.prop('checkin'))
)

export const getHasVisibleDepartureDatePicker = createSelector(
  getActivePackage,
  hasVisibleDepartureDatePicker
)

export const getDepartureDateFromLocalStorage = createSelector(
  getFromLocalStorage,
  R.path(['configurator', 'checkin'])
)

export const getIsDepartureDateFromLSAvailable = createSelector(
  getDepartureDateFromLocalStorage,
  getPackageElements,
  (departureDateFromLS, packageElements) => {
    if (departureDateFromLS) {
      const packageElementsFilteredBySelection = getCurrentPackageFilteredDepartureDates(packageElements)

      return packageElementsFilteredBySelection.reduce((acc, value) => {
        if (!acc) {
          acc = (value.stock && departureDateFromLS === value.checkin)
        }
        return acc
      }, false)
    }
    return false
  }
)

export const getCheckinFromPriceInfos = createSelector(
  getIsToggleCheapestPriceFeatureEnabled,
  getIsCheapestPriceToggled,
  getActivePackagePriceInfos,
  getPriceInfosFromActivePackageInFilteredProducts,
  (isCheapestPriceFeatureEnabled, isCheapestPriceToggled, priceInfos, priceInfosFromFilters) => {
    const checkinFromFilters = (priceInfosFromFilters !== undefined && priceInfosFromFilters.checkin !== '')
      ? priceInfosFromFilters.checkin
      : undefined

    const checkinFromPriceInfos = (priceInfos.checkin !== '')
      ? priceInfos.checkin
      : undefined

    const checkin = checkinFromFilters || checkinFromPriceInfos

    return (
      isCheapestPriceFeatureEnabled && isCheapestPriceToggled && checkin !== undefined
        ? checkin
        : null
    )
  }
)

export const getIsDepartureDateFromPriceInfosAvailable = createSelector(
  getCheckinFromPriceInfos,
  getPackageElements,
  (departureDateFromPriceInfos, packageElements) => {
    if (departureDateFromPriceInfos) {
      const packageElementsFilteredBySelection = getCurrentPackageFilteredDepartureDates(packageElements)

      return packageElementsFilteredBySelection.reduce((acc, value) => {
        if (!acc) {
          acc = (departureDateFromPriceInfos === value.checkin)
        }
        return acc
      }, false)
    }
    return false
  }
)

export const getDepartureDateFromPriceInfos = createSelector(
  getIsDepartureDateFromPriceInfosAvailable,
  getCheckinFromPriceInfos,
  (isDepartureDateFromPriceInfosAvailable, departureDateFromPriceInfos) => (
    isDepartureDateFromPriceInfosAvailable
      ? departureDateFromPriceInfos
      : null
  )
)

export const getDepartureDateFromLS = createSelector(
  getIsDepartureDateFromLSAvailable,
  getDepartureDateFromLocalStorage,
  (isDepartureDateFromLSAvailable, departureDateFromLS) => (
    isDepartureDateFromLSAvailable
      ? departureDateFromLS
      : null
  )
)

export const getUniqDepartureDate = createSelector(
  getAvailableDepartureDates,
  getHasOnlyOneDepartureDate,
  (availableDepartureDate, hasOnlyOneDepartureDate) => (
    hasOnlyOneDepartureDate && availableDepartureDate[0].stock
      ? availableDepartureDate[0].checkin
      : null
  )
)

export const getDepartureDateAutoSelect = createSelector(
  getDepartureDateFromPriceInfos,
  getHasVisibleDepartureDatePicker,
  getSelectedCheckin,
  getDepartureDateFromLS,
  getUniqDepartureDate,
  (departureDateFromPriceInfos, hasVisiblePicker, selectedCheckin, departureDateFromLS, departureDateAutoSelect) => (
    hasVisiblePicker && !selectedCheckin
      ? departureDateFromPriceInfos || departureDateFromLS || departureDateAutoSelect
      : null
  )
)
